import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { marked, type Tokens } from 'marked';
import type { TechDomain } from '../../data/techDomains';
import { CATEGORY_LABEL } from '../../data/techDomains';
import './TechWikiModal.css';
import './TechWikiColumns.css';

interface TreeNode {
  id: string;
  text: string;
  children: TreeNode[];
  level: number;
}

interface Props {
  domain: TechDomain;
  onClose: () => void;
}

/* ---------- markdown → 多级树解析 ---------- */
let _uid = 0;
const parseListItems = (items: Tokens.ListItem[], level: number): TreeNode[] => {
  return items.map((it) => {
    const topTokens = (it.tokens || []).filter((t) => t.type !== 'list');
    const nestedList = (it.tokens || []).find((t) => t.type === 'list') as Tokens.List | undefined;
    const text = marked.parser(topTokens as any).replace(/^<p>|<\/p>\s*$/g, '').trim();
    return {
      id: `n-${_uid++}`,
      text,
      level,
      children: nestedList ? parseListItems(nestedList.items, level + 1) : [],
    };
  });
};

const parseSectionBody = (body: string): TreeNode[] => {
  const tokens = marked.lexer(body);
  const roots: TreeNode[] = [];
  let currentH3: TreeNode | null = null;
  let currentH4: TreeNode | null = null;

  for (const tk of tokens) {
    if (tk.type === 'heading' && tk.depth === 3) {
      currentH3 = { id: `n-${_uid++}`, text: marked.parseInline(tk.text) as string, level: 1, children: [] };
      currentH4 = null;
      roots.push(currentH3);
    } else if (tk.type === 'heading' && tk.depth === 4) {
      currentH4 = { id: `n-${_uid++}`, text: marked.parseInline(tk.text) as string, level: 2, children: [] };
      if (currentH3) currentH3.children.push(currentH4);
      else roots.push(currentH4);
    } else if (tk.type === 'list') {
      const listNodes = parseListItems((tk as Tokens.List).items, currentH4 ? 3 : currentH3 ? 2 : 1);
      const target = currentH4?.children ?? currentH3?.children ?? roots;
      target.push(...listNodes);
    } else if (tk.type === 'paragraph') {
      const node: TreeNode = {
        id: `n-${_uid++}`,
        text: marked.parseInline((tk as Tokens.Paragraph).text) as string,
        level: currentH4 ? 3 : currentH3 ? 2 : 1,
        children: [],
      };
      const target = currentH4?.children ?? currentH3?.children ?? roots;
      target.push(node);
    }
  }
  return roots;
};

/* 从 H2 分段建整个域的根级列表: H2 是第 0 层 */
const buildDomainTree = (raw: string): TreeNode[] => {
  const parts = raw.split(/^## /m).slice(1);
  return parts.map((p, idx) => {
    const [firstLine, ...rest] = p.split('\n');
    return {
      id: `h2-${idx}`,
      text: firstLine.trim(),
      level: 0,
      children: parseSectionBody(rest.join('\n')),
    };
  });
};

/* ---------- 列式浏览器 ---------- */
export const TechWikiModal: React.FC<Props> = ({ domain, onClose }) => {
  const [roots, setRoots] = useState<TreeNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // path[i] = 第 i 列选中的节点；用它决定第 i+1 列的内容
  const [path, setPath] = useState<TreeNode[]>([]);
  const columnsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  useEffect(() => {
    if (!domain.mdFile) {
      setLoading(false);
      setError('该领域暂未提供详细知识文档');
      return;
    }
    (async () => {
      try {
        const resp = await fetch(`/data/${encodeURIComponent(domain.mdFile!)}.md`);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const raw = await resp.text();
        const tree = buildDomainTree(raw);
        setRoots(tree);
        // 默认选中第一个 H2
        if (tree.length > 0) setPath([tree[0]]);
        setLoading(false);
      } catch (e) {
        console.error(e);
        setError('文档加载失败');
        setLoading(false);
      }
    })();
  }, [domain.mdFile]);

  // 点击某列的某个节点：截断到该列 + 追加新节点
  // 叶子节点（无 children）不响应点击，避免弹出"已到达叶子节点"的冗余提示
  const handleSelect = (colIdx: number, node: TreeNode) => {
    if (node.children.length === 0) return;
    const next = [...path.slice(0, colIdx), node];
    setPath(next);
    // 滚动列容器到最右
    requestAnimationFrame(() => {
      if (columnsRef.current) {
        columnsRef.current.scrollTo({
          left: columnsRef.current.scrollWidth,
          behavior: 'smooth',
        });
      }
    });
  };

  // 计算要渲染的列列表
  const columns: TreeNode[][] = [];
  if (roots.length > 0) {
    columns.push(roots); // 第 0 列：所有 H2
    for (let i = 0; i < path.length; i++) {
      const node = path[i];
      if (node.children.length > 0) {
        columns.push(node.children);
      }
    }
  }

  // 面包屑
  const breadcrumb = path.map((n) => n.text.replace(/<[^>]+>/g, ''));

  // 用 Portal 挂到 document.body,避开 FullPage 上的 transform (会让 position:fixed 相对 section 定位)
  return createPortal(
    <div className="wiki-overlay" onClick={onClose} data-testid="tech-wiki-modal">
      <div
        className={`wiki-modal wiki-modal-cols wiki-cat--${domain.category}`}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="wiki-header">
          <div className="wiki-header-icon">
            <i className={`fa-solid ${domain.icon}`} />
          </div>
          <div className="wiki-header-body">
            <div className="wiki-header-cat">{CATEGORY_LABEL[domain.category]}</div>
            <h2 className="wiki-header-title">{domain.name}</h2>
            <p className="wiki-header-summary">{domain.summary}</p>
          </div>
          <div className="wiki-header-score">
            <span className="wiki-score-num">{domain.score}</span>
            <span className="wiki-score-max">/100</span>
          </div>
          <button className="wiki-close" onClick={onClose} aria-label="关闭">
            <i className="fa-solid fa-xmark" />
          </button>
        </header>

        {/* 面包屑 */}
        {breadcrumb.length > 0 && (
          <div className="wiki-breadcrumb">
            <i className="fa-solid fa-diagram-project" />
            {breadcrumb.map((crumb, i) => (
              <React.Fragment key={i}>
                <button
                  className={`wiki-crumb ${i === breadcrumb.length - 1 ? 'active' : ''}`}
                  onClick={() => setPath(path.slice(0, i + 1))}
                  dangerouslySetInnerHTML={{ __html: crumb }}
                />
                {i < breadcrumb.length - 1 && (
                  <i className="fa-solid fa-chevron-right wiki-crumb-sep" />
                )}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* 列式浏览容器 */}
        <div className="wiki-columns" ref={columnsRef}>
          {loading && <div className="wiki-loading">加载中…</div>}
          {error && <div className="wiki-error">{error}</div>}
          {!loading && !error && columns.map((items, colIdx) => {
            const selectedNode = path[colIdx];
            return (
              <div key={colIdx} className={`wiki-col wiki-col--${colIdx}`}>
                <div className="wiki-col-label">
                  {colIdx === 0 ? '章节' : `第 ${colIdx} 级`}
                  <span className="wiki-col-count">{items.length}</span>
                </div>
                <div className="wiki-col-list">
                  {items.map((item) => {
                    const hasChildren = item.children.length > 0;
                    const isSelected = selectedNode?.id === item.id;
                    return (
                      <button
                        key={item.id}
                        className={`wiki-col-item ${isSelected ? 'selected' : ''} ${hasChildren ? 'has-children' : 'leaf'}`}
                        onClick={() => handleSelect(colIdx, item)}
                        type="button"
                        disabled={!hasChildren && colIdx > 0}
                        aria-disabled={!hasChildren && colIdx > 0}
                      >
                        <span
                          className="wiki-col-text"
                          dangerouslySetInnerHTML={{ __html: item.text }}
                        />
                        {hasChildren && (
                          <>
                            <span className="wiki-col-count-mini">{item.children.length}</span>
                            <i className="fa-solid fa-chevron-right wiki-col-caret" />
                          </>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer: 关键词 */}
        {domain.keywords && domain.keywords.length > 0 && (
          <div className="wiki-footer-kws">
            <span className="wiki-footer-label">关键词</span>
            {domain.keywords.map((k) => (
              <span key={k} className="wiki-footer-kw">{k}</span>
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default TechWikiModal;
